/**
 * Secure redirect utility to prevent Cross-Origin Opener Policy attacks
 * and malicious redirects during OAuth flows
 */

// Allowed domains for redirects
const ALLOWED_DOMAINS = [
  'bake.io',
  'app.bake.io',
  'app.muffindefi.com',
  'localhost',
  'accounts.google.com',
  'appleid.apple.com',
  'oauth2.razer.com',
  'support.bake.io',
  'checkout.transak.com',
  'checkout.banxa.com',
  'wa.me',
  'defichain.com',
  'muffindefi.com',
];

/**
 * Validates if a URL is safe for redirection
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is safe, false otherwise
 */
export function isValidRedirectUrl(url) {
  try {
    const urlObj = new URL(url);

    // Check if it's a relative URL (safe)
    if (url.startsWith('/') && !url.startsWith('//')) {
      return true;
    }

    // Check if it's a mailto or tel link (safe)
    if (urlObj.protocol === 'mailto:' || urlObj.protocol === 'tel:') {
      return true;
    }

    // Check if domain is in allowed list
    const hostname = urlObj.hostname.toLowerCase();
    return ALLOWED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch (error) {
    console.error('Invalid URL provided to isValidRedirectUrl:', url);
    return false;
  }
}

/**
 * Safely redirects to a URL after validation
 * @param {string} url - The URL to redirect to
 * @param {boolean} newWindow - Whether to open in new window (default: false)
 * @param {string} windowFeatures - Window features for new window
 */
export function secureRedirect(url, newWindow = false, windowFeatures = 'noopener,noreferrer') {
  if (!isValidRedirectUrl(url)) {
    throw new Error('Invalid redirect URL detected');
  }

  if (newWindow) {
    // Ensure secure window features
    const secureFeatures = windowFeatures.includes('noopener') ? windowFeatures : `${windowFeatures},noopener,noreferrer`;
    return window.open(url, '_blank', secureFeatures);
  }
  // Check if we're in a potentially compromised context
  if (window.opener && window.opener !== window) {
    window.location.replace(url);
    return;
  }
  window.location.href = url;
}

/**
 * Validates the current window context for security
 * @returns {boolean} - True if context appears secure
 */
export function validateWindowContext() {
  // Check if window has been opened by another window
  if (window.opener && window.opener !== window) {
    console.error('SECURITY ALERT: Window opened by external source detected. Potential COOP attack blocked.');
    try {
      window.close();
    } catch (e) {
      window.location.replace('about:blank');
    }
    return false;
  }

  if (window.name && window.name.length > 0) {
    window.name = '';
  }

  return true;
}

/**
 * Initialize security checks on page load
 */
export function initializeSecurityChecks() {
  // Validate window context immediately
  if (!validateWindowContext()) {
    // If context is compromised, stop execution
    return;
  }

  // Clear any potentially malicious window.name
  if (window.name) {
    window.name = '';
  }

  // Override window.open to use secure version
  const originalOpen = window.open;
  // eslint-disable-next-line func-names
  window.open = function (url, target, features) {
    if (url && !isValidRedirectUrl(url)) {
      console.error('SECURITY ALERT: Blocked window.open to unauthorized domain:', url);
      return null;
    }

    // Ensure secure features
    const secureFeatures = features && features.includes('noopener') ? features : `${features || ''},noopener,noreferrer`;
    return originalOpen.call(this, url, target, secureFeatures);
  };
}
